import { useState } from "react";
import { useApi } from "../../api/client";
import { tasks as tasksContract } from "../../api/contracts/tasks";
import { Button } from "../../button";
import { Decision } from "../decision";
import { PeekDialogSection } from "../peek-dialog";
import { formatDateTime } from "../../format";

/**
 * P2-U3 (verification-loop Wave B-UI) — the task peek's verification-evidence
 * section. Renders ONLY when the P2-02 production emitter has appended an
 * `evidence.artifact` row to the task's agent-run trajectory (i.e. the task
 * reached a verified done), so a task without one shows nothing rather than a
 * heading over a void.
 *
 * Deliberately a composition, not a new surface: the resolved two-party gate
 * is the existing `Decision` block in read-only mode, and the artifact
 * affordance rides its evidence slot — open/copy of the artifact RESOURCE
 * (the contract GET whose response is the scrubbed document), not a custom
 * evidence viewer.
 */
export function TaskEvidenceArtifactSection({ taskId }: { taskId: number }) {
  const { data } = useApi(tasksContract.evidenceArtifact, {
    params: { id: taskId },
  });
  const [copied, setCopied] = useState(false);

  if (!data?.found) return null;

  const href = tasksContract.evidenceArtifact.path({ id: taskId });
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        new URL(href, window.location.origin).toString(),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (non-secure origin) — the Open link still works.
    }
  };

  return (
    <PeekDialogSection title="Verification evidence">
      <Decision
        readOnly
        verdict="accepted"
        ownerLabel={data.shipper}
        verifierLabel={data.verifier}
        evidence={
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate">
              Evidence artifact · session {data.sessionUid ?? data.sessionId}
              {data.seq != null ? ` · seq ${data.seq}` : ""}
              {data.caller ? ` · via ${data.caller}` : ""}
              {data.emittedAt ? ` · ${formatDateTime(data.emittedAt)}` : ""}
            </span>
            <Button variant="ghost" size="xs" asChild>
              <a href={href} target="_blank" rel="noreferrer">
                Open
              </a>
            </Button>
            <Button variant="ghost" size="xs" onClick={copyLink}>
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        }
      />
    </PeekDialogSection>
  );
}
